import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Mail, ArrowLeft, CheckCircle } from 'lucide-react';
import { authApi } from '@/api/auth.api';
import { AppRoutes } from '@/constants/routes';

export const ForgotPasswordPage: React.FC = () => {
  const [email, setEmail]     = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent]       = useState(false);
  const [error, setError]     = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) return;
    setLoading(true);
    setError('');
    try {
      await authApi.forgotPassword(email.trim());
      setSent(true);
    } catch {
      setError('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (sent) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-800/50 px-4">
        <div className="w-full max-w-md bg-white dark:bg-slate-800 rounded-lg shadow p-8 text-center">
          <CheckCircle className="h-12 w-12 text-green-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold mb-2">Check your inbox</h2>
          <p className="text-gray-600 dark:text-gray-400 mb-6">
            If <strong>{email}</strong> is registered, a reset code has been sent.
            Check the server logs if running in development mode.
          </p>
          <Link
            to={AppRoutes.RESET_PASSWORD}
            state={{ email }}
            className="block w-full text-center bg-blue-600 text-white py-2 px-4 rounded hover:bg-blue-700 mb-3"
          >
            Enter Reset Code
          </Link>
          <Link to={AppRoutes.LOGIN} className="text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700">
            Back to Login
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-800/50 px-4">
      <div className="w-full max-w-md bg-white dark:bg-slate-800 rounded-lg shadow p-8">
        <div className="flex items-center gap-2 mb-6">
          <Mail className="h-6 w-6 text-blue-600" />
          <h1 className="text-xl font-semibold">Forgot Password</h1>
        </div>
        <p className="text-gray-600 dark:text-gray-400 text-sm mb-6">
          Enter your email address and we'll send you a password reset code.
        </p>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Email Address
            </label>
            <input
              type="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              required
              autoFocus
              className="w-full border border-gray-300 dark:border-gray-600 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="you@company.com"
            />
          </div>
          {error && <p className="text-red-600 dark:text-red-400 text-sm">{error}</p>}
          <button
            type="submit"
            disabled={loading}
            className="w-full bg-blue-600 text-white py-2 px-4 rounded hover:bg-blue-700 disabled:opacity-50 text-sm font-medium"
          >
            {loading ? 'Sending…' : 'Send Reset Code'}
          </button>
        </form>
        <div className="mt-4 text-center">
          <Link
            to={AppRoutes.LOGIN}
            className="inline-flex items-center gap-1 text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700"
          >
            <ArrowLeft className="h-3 w-3" /> Back to Login
          </Link>
        </div>
      </div>
    </div>
  );
};
